import React, { useState, } from 'react';
import FlIntegrationService from '../../../service/FlIntegrationService';
import CitationsService from '../../../service/CitationsService';
import WaterService from '../../../service/WaterService';
import CitationsRetryForm from './CitationsRetryForm';
import FlDmvRetryForm from './FlDmvRetryForm';
import WaterRetryForm from './WaterRetryForm';

const SERVICE_OPTIONS = [
  { value: '', display: 'Select a service...', },
  { value: 'citations', display: 'Citations Service', },
  { value: 'fldmv', display: 'FL DMV', },
  { value: 'water', display: 'Water Service', },
];

const RUNBOOK_URL = 'https://payitdev.atlassian.net/wiki/spaces/MT/pages/1039303733/Runbook';

const SERVICE_DOCS = {
  fldmv: {
    title: 'FL DMV — Refresh & Repost Vehicle Renewal',
    description: 'Reposts a previously failed vehicle renewal to Florida\'s DHSMV API.',
    whenToUse: 'The FL DMV remittance checker is failing because a renewal exists but is missing its HSMV receipt number — meaning it never successfully posted to Florida.',
    behavior: [
      'Looks up the renewal by transaction ID.',
      'Validates the renewal is marked as failed by Florida and has not been refunded.',
      'Refreshes expired credentials needed for the repost.',
      'Reposts the renewal to FL DHSMV and sends receipts on success.',
    ],
    notes: 'Returns 400 if the renewal is not in a failed state, has been refunded, or is not an fldmv transaction. Returns 404 if the transaction ID is not found.',
  },
  citations: {
    title: 'Citations — Retry Payment Submission',
    description: 'Replays a citation payment transaction against the client\'s external API to obtain the external receipt ID.',
    whenToUse: 'The payment was recorded internally but never successfully posted to the client\'s external API.',
    behavior: [
      'Creates a payment submission task and places it on the durable queue.',
      'Processing happens asynchronously (up to 5 retries, 2s apart).',
      'Retries the final "post to client API" step — does NOT re-charge the customer.',
      'The queue deduplicates by transaction ID — repeated calls are safe.',
    ],
    notes: 'Always returns 200 with an empty body ({}). The actual success or failure of reprocessing happens in the background.',
  },
  water: {
    title: 'Water Service — Manual Payment Retry',
    description: 'Retries posting a water bill payment to the client\'s external system. "Entire Transaction" retries all line items at once. "Single Line Item" targets one specific line item + external payable.',
    whenToUse: 'The payment was recorded internally but never successfully posted to the client\'s billing system.',
    behavior: [
      'Calls applyPayment for the transaction (or a specific line item).',
      'Synchronous — single attempt, returns the result directly.',
      'Does NOT re-charge the customer — retries the "post to client" step only.',
    ],
    notes: 'Returns the updated WaterBill(s) on success. forceRepostToClient will repost even if the payment was already acknowledged.',
  },
};

const FORM_COMPONENT = {
  fldmv: FlDmvRetryForm,
  citations: CitationsRetryForm,
  water: WaterRetryForm,
};

const SERVICE_SUBMIT = {
  fldmv: (payload) => FlIntegrationService.retryRegistration(payload.txnId),
  citations: (payload) => CitationsService.retryPayment(payload.txnId),
  water: (payload) => payload.subType === 'all'
    ? WaterService.retryManualAll(payload)
    : WaterService.retryManualSingle(payload),
};

const ManualRetryView = () => {
  const [selectedService, setSelectedService] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const onServiceChange = (e) => {
    setSelectedService(e.target.value);
    setResult(null);
  };

  const onSubmit = (payload) => {
    setLoading(true);
    setResult(null);

    SERVICE_SUBMIT[selectedService](payload)
      .then((response) => {
        setResult(response.data);
      })
      .catch((err) => {
        setResult({
          url: 'unknown',
          status: err.response?.status || 500,
          body: err.response?.data || err.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderDocs = () => {
    if (!selectedService) return null;
    const docs = SERVICE_DOCS[selectedService];
    if (!docs) return null;

    return (
      <div className='mt-3 mb-3'>
        <div className='card'>
          <div className='card-body'>
            <h6 className='card-title'>{docs.title}</h6>
            <p className='card-text'>{docs.description}</p>
            <p className='mb-1'><strong>When to use:</strong> {docs.whenToUse}</p>
            <p className='mb-1'><strong>What happens:</strong></p>
            <ol className='mb-1'>
              {docs.behavior.map((step, i) => <li key={i}>{step}</li>)}
            </ol>
            <p className='mb-0 text-muted'><small>{docs.notes}</small></p>
          </div>
        </div>
      </div>
    );
  };

  const renderResult = () => {
    if (!result) return null;

    const isSuccess = result.status >= 200 && result.status < 300;
    const statusColor = isSuccess ? 'var(--bs-success)' : 'var(--bs-danger)';

    return (
      <div className='mt-4'>
        <h6>Result</h6>
        <table className='table table-bordered table-sm'>
          <tbody>
            <tr>
              <td><strong>URL</strong></td>
              <td><code>{result.url}</code></td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td><span style={{ color: statusColor, fontWeight: 'bold', }}>{result.status}</span></td>
            </tr>
          </tbody>
        </table>
        <div>
          <strong>Response Body</strong>
          <pre className='mt-2 p-3' style={{ background: '#f5f5f5', maxHeight: '400px', overflow: 'auto', }}>
            {typeof result.body === 'string' ? result.body : JSON.stringify(result.body, null, 2)}
          </pre>
        </div>
      </div>
    );
  };

  const FormComponent = FORM_COMPONENT[selectedService];

  return (
    <div className='container pt-4 pb-5'>
      <h5>Manual Retry (for Remittance Errors)</h5>
      <p className='text-muted mb-1'>
        <span>Payment collected <span style={{ color: 'green', }}>&#10003;</span></span>
        {' \u2192 '}
        <span>Client notified <span style={{ color: 'red', }}>&#10007;</span></span>
      </p>
      <p className='text-muted'>
        Repost a transaction record to the client&#39;s system.
      </p>
      <p className='mb-3'>
        <a href={RUNBOOK_URL} target='_blank' rel='noopener noreferrer'>
          Remittance Error Runbooks
        </a>
      </p>
      <div className='mb-3'>
        <label htmlFor='serviceSelect'>Service</label>
        <select
          id='serviceSelect'
          className='form-select'
          value={selectedService}
          onChange={onServiceChange}
        >
          {SERVICE_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.display}</option>
          ))}
        </select>
      </div>
      {renderDocs()}
      {FormComponent && (
        <FormComponent
          key={selectedService}
          loading={loading}
          onSubmit={onSubmit}
          onReset={() => setResult(null)} />
        )}
      {renderResult()}
    </div>
  );
};

export default ManualRetryView;
